'use client';

import React from 'react';
import { useColors } from '../theme/ThemeProvider';
import { fontSize, fontWeight, motion, radius, space } from '../theme/tokens';

export interface BarProps {
  label: string;
  value: number;
  target: number;
  /** Override bar fill color. Defaults to theme accent. */
  color?: string;
  /** Unit label (e.g. "g", "kcal"). Defaults to "g". */
  unit?: string;
}

/**
 * Labeled linear progress bar.
 * Shows `{value} / {target} {unit}` above the bar.
 */
export function Bar({ label, value, target, color, unit = 'g' }: BarProps) {
  const colors = useColors();
  const barColor = color ?? colors.accent;
  const pct = target > 0 ? Math.min(100, (value / target) * 100) : 0;

  const formatNumber = (n: number) => Math.round(n).toLocaleString('de-DE');

  return (
    <div style={{ marginBottom: space[3] }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          fontSize: fontSize.base,
          marginBottom: space[1],
        }}
      >
        <span style={{ fontWeight: fontWeight.semibold, color: colors.text }}>
          {label}
        </span>
        <span style={{ color: colors.textDim }}>
          {formatNumber(value)} / {formatNumber(target)} {unit}
        </span>
      </div>
      <div
        style={{
          height: 8,
          background: colors.bg,
          borderRadius: radius.sm / 2,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            height: '100%',
            width: `${pct}%`,
            background: barColor,
            borderRadius: radius.sm / 2,
            transition: `width ${motion.duration.slow}ms ${motion.easing.out}, background ${motion.duration.base}ms`,
          }}
        />
      </div>
    </div>
  );
}
